const SUBUNITS_PER_SWIPA = 100_000n;
const DECIMAL_INTEGER = /^-?\d+$/;
const SWIPA_DECIMAL_INPUT = /^(?:(\d+)(?:\.(\d{1,5}))?|\.(\d{1,5}))$/;

export type SubunitValue = string | number | bigint | null | undefined;

export interface SwipaDistributionCalculation {
  requestedTotalSub: string;
  recipients: number;
  perRecipientSub: string;
  actualTotalSub: string;
  undistributedSub: string;
}

function normalizeSubunits(value: SubunitValue): string | null {
  if (typeof value === 'bigint') return value.toString();
  if (typeof value === 'string' && DECIMAL_INTEGER.test(value)) {
    return BigInt(value).toString();
  }
  if (typeof value === 'number' && Number.isSafeInteger(value)) {
    return BigInt(value).toString();
  }
  return null;
}

/** Prefer the exact decimal API field, retaining a legacy-number fallback. */
export function resolveSubunits(exact: SubunitValue, legacy: SubunitValue = 0): string {
  return normalizeSubunits(exact) ?? normalizeSubunits(legacy) ?? '0';
}

export function subtractSubunits(left: SubunitValue, right: SubunitValue): string {
  return (BigInt(resolveSubunits(left)) - BigInt(resolveSubunits(right))).toString();
}

export function isPositiveSubunits(value: SubunitValue): boolean {
  return BigInt(resolveSubunits(value)) > 0n;
}

/**
 * Normalize one decimal comma to a dot without deleting or reinterpreting any
 * other character. Mixed or repeated separators are invalid.
 */
export function normalizeSwipaDecimalInput(input: string): string | null {
  const separators = input.match(/[.,]/g) ?? [];
  if (separators.length > 1) return null;

  const normalized = separators[0] === ',' ? input.replace(',', '.') : input;
  return SWIPA_DECIMAL_INPUT.test(normalized) ? normalized : null;
}

/**
 * Convert a user-entered SWIPA decimal to canonical sub-units using only
 * strings and BigInt. SWIPA has exactly five sub-unit decimal places; excess
 * precision is rejected instead of silently rounded.
 */
export function parseSwipaToSubunits(input: string): string | null {
  const normalized = normalizeSwipaDecimalInput(input);
  if (normalized === null) return null;

  const match = SWIPA_DECIMAL_INPUT.exec(normalized);
  if (!match) return null;

  const whole = match[1] ?? '0';
  const fraction = match[2] ?? match[3] ?? '';
  const fractionSubunits = fraction.padEnd(5, '0');
  return (
    BigInt(whole) * SUBUNITS_PER_SWIPA
    + BigInt(fractionSubunits || '0')
  ).toString();
}

/** Mirror the server's exact integer split for the pre-submit confirmation. */
export function calculateSwipaDistribution(
  requestedTotalSub: SubunitValue,
  recipients: number,
): SwipaDistributionCalculation | null {
  const normalizedTotal = normalizeSubunits(requestedTotalSub);
  if (
    normalizedTotal === null
    || !Number.isSafeInteger(recipients)
    || recipients <= 0
  ) return null;

  const requested = BigInt(normalizedTotal);
  if (requested <= 0n) return null;

  const recipientCount = BigInt(recipients);
  const perRecipient = requested / recipientCount;
  if (perRecipient <= 0n) return null;

  const actualTotal = perRecipient * recipientCount;
  return {
    requestedTotalSub: requested.toString(),
    recipients,
    perRecipientSub: perRecipient.toString(),
    actualTotalSub: actualTotal.toString(),
    undistributedSub: (requested - actualTotal).toString(),
  };
}

/** Format integer sub-units without ever passing through a JS number. */
export function formatSwipaSubunits(value: SubunitValue): string {
  const raw = BigInt(resolveSubunits(value));
  const negative = raw < 0n;
  const absolute = negative ? -raw : raw;
  const whole = absolute / SUBUNITS_PER_SWIPA;
  const fraction = absolute % SUBUNITS_PER_SWIPA;
  const sign = negative ? '-' : '';
  if (fraction === 0n) return `${sign}${whole}`;
  const fractionText = fraction.toString().padStart(5, '0').replace(/0+$/, '');
  return `${sign}${whole}.${fractionText}`;
}
